import { z } from 'zod';
import { IsoDate, IsoDateTime, UserRef, Uuid, Version, boundedList } from './common.js';
import { WORK_SORTS, WorkListQuery } from './work.js';

/** How work statistics group: a built-in property, or a select / yes-no custom field (field:<id>). */
export const WORK_STAT_GROUPS = ['phase', 'priority', 'type', 'assignee', 'owner', 'project', 'team', 'label'] as const;
export const WorkStatGroup = z.union([z.enum(WORK_STAT_GROUPS), z.string().regex(/^field:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)]);
export type WorkStatGroup = z.infer<typeof WorkStatGroup>;

/** Work list filters without paging or order: what a widget counts. */
export const WorkStatFilters = WorkListQuery.omit({ cursor: true, limit: true, sort: true, dir: true });
export type WorkStatFilters = z.infer<typeof WorkStatFilters>;

export const WorkStatsQuery = WorkStatFilters.extend({ groupBy: WorkStatGroup.optional() });
export type WorkStatsQuery = z.infer<typeof WorkStatsQuery>;

export const WorkStats = z.object({
  total: z.number().int(),
  /**
   * Groups by count, largest first (at most 50). key is the raw value (phase, user id, option id…)
   * or null for "none"; label is a display name for people, projects, teams, labels and options.
   */
  groups: z.array(z.object({ key: z.string().nullable(), label: z.string().nullable(), count: z.number().int() })),
});
export type WorkStats = z.infer<typeof WorkStats>;

export const WorkTrendQuery = WorkStatFilters.extend({ weeks: z.string().regex(/^\d{1,2}$/).optional() });
export type WorkTrendQuery = z.infer<typeof WorkTrendQuery>;

/** Items created and completed per week (weeks start on Monday, deployment time zone). */
export const WorkTrend = z.object({ weeks: z.array(z.object({ weekStart: IsoDate, created: z.number().int(), completed: z.number().int() })) });
export type WorkTrend = z.infer<typeof WorkTrend>;

export const DashboardWidgetKind = z.enum(['COUNT', 'BAR', 'PIE', 'LIST', 'TREND']);
export type DashboardWidgetKind = z.infer<typeof DashboardWidgetKind>;

export const DashboardWidget = z
  .object({
    id: z.string().min(1).max(40),
    kind: DashboardWidgetKind,
    title: z.string().trim().min(1).max(80),
    /** Work list filters as URL values (the same ones the work list uses). */
    filters: z.record(z.string(), z.string().max(800)),
    /** BAR and PIE: how to group. */
    groupBy: WorkStatGroup.optional(),
    /** LIST: how many items and in which order. */
    limit: z.number().int().min(1).max(20).optional(),
    sort: z.enum(WORK_SORTS).optional(),
    /** TREND: how many weeks back. */
    weeks: z.number().int().min(4).max(26).optional(),
    size: z.enum(['S', 'M', 'L']),
  })
  .strict();
export type DashboardWidget = z.infer<typeof DashboardWidget>;

export const DashboardView = z.object({
  id: Uuid,
  name: z.string(),
  description: z.string(),
  owner: UserRef.nullable(),
  shared: z.boolean(),
  widgets: z.array(DashboardWidget),
  version: Version,
  /** The owner and administrators edit a dashboard; everyone else can copy it. */
  canEdit: z.boolean(),
  updatedAt: IsoDateTime,
});
export type DashboardView = z.infer<typeof DashboardView>;

export const CreateDashboard = z
  .object({
    name: z.string().trim().min(1).max(80),
    description: z.string().max(300).optional(),
    shared: z.boolean().optional(),
    widgets: boundedList(DashboardWidget, 24).optional(),
  })
  .strict();
export type CreateDashboard = z.infer<typeof CreateDashboard>;

export const UpdateDashboard = z
  .object({
    expectedVersion: Version,
    name: z.string().trim().min(1).max(80).optional(),
    description: z.string().max(300).optional(),
    shared: z.boolean().optional(),
    widgets: boundedList(DashboardWidget, 24).optional(),
  })
  .strict();
export type UpdateDashboard = z.infer<typeof UpdateDashboard>;
